/**
 * `fetch` that is guaranteed to settle.
 *
 * ── Why this exists (2026-08-21, measured on production) ──────────────────
 * A real anonymous browser session on a job page recorded:
 *
 *   /api/monetization/guardrails   start 12.76s   duration 79,180ms
 *
 * Seventy-nine seconds. The route itself is not the problem — sampled
 * server-side it answers in 0.33–0.51s warm (`x-nextjs-cache: HIT`), with
 * occasional multi-second cold spikes. What made it 79s was the CLIENT: the
 * request was queued behind a third-party widget's 32-request cascade, and
 * nothing on our side bounded the wait.
 *
 * That mattered because both of this project's page-wide client caches are
 * shaped the same way — one module-level promise shared by every consumer on
 * the page:
 *
 *   fetchGuardrailConfig()  (src/lib/monetization/use-ad-guardrails.ts)
 *   fetchNavbarData()       (src/lib/user/navbar-data-client.ts)
 *
 * The sharing is correct and deliberate (N ad units + the navbar fire ONE
 * request, not N). But it also means a single stalled request holds every
 * consumer in its not-ready state for as long as the stall lasts. Both call
 * sites carried a `.catch()` and a documented "degrades to the floor, never
 * blocks rendering" guarantee — and that guarantee only ever held for a
 * REJECTED fetch. A hanging one is not an error; it is silence, and `.catch()`
 * never runs. The failure mode the comment promised to handle was the one it
 * could not see.
 *
 * So: an explicit deadline. On timeout the promise RESOLVES to the caller's
 * fallback rather than rejecting, because every one of these call sites has a
 * safe code-default floor and a page that renders fine without the network
 * answer. A slow network should cost a visitor the tuned value, never the UI.
 *
 * Uses a manual `AbortController` + timer rather than `AbortSignal.timeout()`:
 * the latter is unsupported on Safari < 16 and older Android WebViews, which
 * are exactly the slow, contended clients this protects.
 */

/**
 * Default deadline for a page-wide config fetch.
 *
 * Chosen against the measured server-side distribution (0.33–0.51s warm, cold
 * spikes to ~9s): long enough that an ordinary cold start still returns the
 * real answer, short enough that a genuinely stuck request degrades before a
 * visitor notices. Callers on a render-blocking path should pass something
 * tighter.
 */
export const DEFAULT_FETCH_TIMEOUT_MS = 6_000;

export interface FetchWithTimeoutOptions extends RequestInit {
	timeoutMs?: number;
}

/**
 * `fetch` with a hard deadline. Rejects (like `fetch`) on network error or
 * abort — see `fetchJsonOrFallback` for the resolve-to-a-floor variant that
 * both page-wide caches actually want.
 */
export async function fetchWithTimeout(
	input: RequestInfo | URL,
	{
		timeoutMs = DEFAULT_FETCH_TIMEOUT_MS,
		...init
	}: FetchWithTimeoutOptions = {},
): Promise<Response> {
	const controller = new AbortController();
	const timer = setTimeout(() => {
		controller.abort(new DOMException("Timeout", "TimeoutError"));
	}, timeoutMs);
	try {
		return await fetch(input, { ...init, signal: controller.signal });
	} finally {
		// Always cleared, including on the success path — a pending timer would
		// otherwise abort nothing but keep the page's event loop busy, and in
		// jsdom would keep a test's fake clock alive past the assertion.
		clearTimeout(timer);
	}
}

/**
 * Fetches JSON and NEVER rejects: a non-2xx status, a network error, an
 * unparseable body, or a blown deadline all resolve to `fallback`.
 *
 * This is the contract the page-wide caches were documented to have. Returning
 * the floor is a real answer — the code defaults are the same values the
 * server would have merged the override onto — so a consumer that receives it
 * renders correctly, just untuned.
 */
export async function fetchJsonOrFallback<T>(
	input: RequestInfo | URL,
	fallback: T,
	options: FetchWithTimeoutOptions = {},
): Promise<T> {
	try {
		const res = await fetchWithTimeout(input, options);
		if (!res.ok) return fallback;
		return (await res.json()) as T;
	} catch {
		return fallback;
	}
}

/**
 * How long a client-side unit may wait for a *resolution* (a feature flag, a
 * session, a consent record) before it must proceed on its safe default.
 *
 * The apply CTA set this at 1.5 s for `apply-guest-no-signup` (PR #4215's
 * follow-up, as its own `GUEST_FLAG_RESOLVE_TIMEOUT_MS` literal, because
 * `src/components` must not grow an import boundary into `net` per
 * `check:deep-imports`). **That call site is gone as of 2026-09-07** — the
 * experiment was retired at its shipped arm and the timeout deleted with it,
 * because "proceed on the safe default" was the wrong instruction there: the
 * default was a signup wall, so a visitor PostHog never answered for got
 * blocked rather than degraded. The deadline itself is unchanged and still
 * right wherever the default is genuinely safe: long enough
 * for a normal PostHog `/decide` or `get-session` round-trip to win the race,
 * short enough that a visitor whose answer never arrives — ad-blocker, a
 * `/decide` outage, a cold isolate on a slow link — is not left staring at a
 * skeleton. The monetization guardrail hook waits on the same deadline for
 * the better-auth session: a reader scrolling a job page is past the ad slot
 * in well under the 2–5 s a cold origin round-trip costs
 * (docs/audit/perf-baseline-2026-09-05.md), so "wait for the session, then
 * start deciding" was the mechanism behind units painting only after the
 * section had been scrolled past.
 */
export const CLIENT_RESOLVE_TIMEOUT_MS = 1_500;
